import axios from 'axios';
import { getAppConfig } from '@srichaitra/config';
import { ApiError } from './ApiError';
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from './tokenStorage';
import { decryptBody, encryptBody, isEncryptedEnvelope, resetPublicKey } from './payloadCrypto';

/**
 * The one HTTP client in the application, and the interceptor pair that every
 * request and response passes through.
 *
 * Five things happen here so that no caller has to think about any of them:
 *
 *   1. The bearer token is attached.
 *   2. The body is encrypted (and the reply decrypted). Doing it in the
 *      interceptor rather than in execute() means *everything* that leaves this
 *      client is covered — including the token refresh below, which would
 *      otherwise put a refresh token on the wire in the clear.
 *   3. A 401 triggers one refresh-and-retry, with concurrent 401s sharing a
 *      single refresh call instead of stampeding the endpoint.
 *   4. A rejected payload key is recovered from once, by re-fetching the
 *      server's public key — otherwise a server-side key rotation would break
 *      every request until the tab is reloaded.
 *   5. Every failure becomes an ApiError, so callers never branch on
 *      response.data.success or dig through an axios error themselves.
 */

// Actions the backend marks auth:false. A 401 from these means the credentials
// were rejected, so refreshing and retrying would just fail a second time.
const NO_REFRESH_ACTIONS = new Set(['auth.login', 'auth.refresh']);

let unauthorizedHandler = null;

/**
 * Registered once by the app shell so the interceptor can trigger a Redux
 * logout without importing the store — which would be a cycle, since the store
 * imports the API.
 */
export function setUnauthorizedHandler(handler) {
  unauthorizedHandler = handler;
}

/**
 * The action a request carries.
 *
 * Read from what the interceptor recorded before encrypting: once the body is
 * an envelope the action is ciphertext, so parsing config.data finds nothing.
 */
function actionOf(config) {
  return config?.__action;
}

/** Replaces an encrypted response envelope with the plaintext it carries. */
async function openResponse(response) {
  const key = response?.config?.__aesKey;
  if (key && isEncryptedEnvelope(response.data)) {
    response.data = await decryptBody(key, response.data);
  }
  return response;
}

let refreshPromise = null;

/** Runs at most one refresh at a time; concurrent 401s share the in-flight promise. */
function refreshAccessToken(client) {
  if (refreshPromise) return refreshPromise;

  const currentRefreshToken = getRefreshToken();
  if (!currentRefreshToken) return Promise.resolve(null);

  refreshPromise = (async () => {
    try {
      const { executePath } = getAppConfig().api;
      const response = await client.post(executePath, {
        action: 'auth.refresh',
        data: { refreshToken: currentRefreshToken }
      });

      if (!response.data?.success || !response.data.data) return null;

      const { accessToken, refreshToken } = response.data.data;
      setTokens({ accessToken, refreshToken });
      return accessToken;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export function createHttpClient() {
  const { baseUrl, basePath, timeoutMs } = getAppConfig().api;
  const { encryptPayloads } = getAppConfig().security;

  const client = axios.create({
    // Includes basePath so callers post to just executePath ("/execute")
    // rather than needing to know the "/api/v1" prefix themselves.
    baseURL: `${baseUrl}${basePath}`,
    timeout: timeoutMs,
    headers: { 'Content-Type': 'application/json' }
  });

  client.interceptors.request.use(async (config) => {
    const token = getAccessToken();
    if (token) config.headers.set('Authorization', `Bearer ${token}`);

    if (!encryptPayloads || config.plaintext) return config;

    // On a retry the earlier pass already swapped config.data for an envelope,
    // so the original body is kept and re-encrypted under a fresh key rather
    // than being wrapped twice.
    const body = config.__plainData ?? config.data;
    if (!body || typeof body !== 'object') return config;

    config.__plainData = body;
    config.__action = body.action;

    const { envelope, aesKey } = await encryptBody(body);
    config.data = envelope;
    config.__aesKey = aesKey;
    return config;
  });

  client.interceptors.response.use(
    (response) => openResponse(response),
    async (error) => {
      const config = error.config;

      // Failures come back encrypted too, so the envelope has to be opened
      // before anything can read the error code out of it.
      if (error.response) {
        try {
          await openResponse(error.response);
        } catch {
          // Undecryptable error body. Fall through to the generic handling
          // below rather than masking the original failure with this one.
        }
      }

      const envelope = error.response?.data;
      const status = error.response?.status ?? 0;
      const action = actionOf(config);

      // The server could not unwrap our key — most likely its payload key pair
      // was regenerated. Drop the cached public key and retry once with the
      // current one, instead of failing every request from here on.
      if (envelope?.error?.code === 'PAYLOAD_DECRYPT_FAILED' && config && !config.__retriedAfterKeyRefresh) {
        config.__retriedAfterKeyRefresh = true;
        resetPublicKey();
        return client.request(config);
      }

      const isAuthFailure = status === 401;
      const canRetry = config && !config.__retriedAfterRefresh && action && !NO_REFRESH_ACTIONS.has(action);

      if (isAuthFailure && canRetry) {
        config.__retriedAfterRefresh = true;
        const newAccessToken = await refreshAccessToken(client);

        if (newAccessToken) {
          config.headers.set('Authorization', `Bearer ${newAccessToken}`);
          return client.request(config);
        }

        clearTokens();
        unauthorizedHandler?.();
      }

      if (envelope?.error) {
        throw new ApiError(envelope.error.code, envelope.error.message, status, envelope.requestId ?? null);
      }
      if (error.response) {
        throw new ApiError('HTTP_ERROR', error.message, status);
      }
      throw ApiError.network(error.message);
    }
  );

  return client;
}

let client = null;

/**
 * The shared client, created on first use.
 *
 * Lazy because it reads the configuration, which is not available until the ini
 * file has loaded. A module-level `createHttpClient()` would run at import time
 * and throw before the app had a chance to load anything.
 */
export function getHttpClient() {
  if (!client) client = createHttpClient();
  return client;
}

/** Drops the client so the next call rebuilds it — used after configuration changes, and in tests. */
export function resetHttpClient() {
  client = null;
}
